/**
 * ExportService — copies the chosen photos into a folder the user picked.
 *
 * Originals are opened read-only and never written back. When a photo carries a
 * rotation the copy gets it baked into its EXIF orientation marker, which leaves
 * the pixel data byte-identical to the original.
 */

import { rotateJpeg } from '../domain/exif-orientation';
import { extensionOf } from '../domain/media-type';
import { uniqueName, type FsDirectory } from '../adapters/fs-port';
import type { BucketRepository, MediaRepository } from '../repositories';
import type { ExportOptions, ExportProgress } from '../types';
import type { DirectoryResolver } from './ports';

export class ExportService {
  constructor(
    private readonly media: MediaRepository,
    private readonly buckets: BucketRepository,
    private readonly resolve: DirectoryResolver,
  ) {}

  async run(
    options: ExportOptions,
    destination: FsDirectory,
    onProgress?: (progress: ExportProgress) => void,
    signal?: { aborted: boolean },
  ): Promise<ExportProgress> {
    const items = this.media.forBuckets(options.projectId, options.bucketIds);
    const bucketNames = new Map(
      this.buckets.list(options.projectId).map((bucket) => [bucket.id, bucket.name]),
    );

    const progress: ExportProgress = {
      phase: 'copying',
      total: items.length,
      done: 0,
      skipped: 0,
      failed: 0,
      errors: [],
    };
    onProgress?.({ ...progress });

    // One folder handle and one name-set per destination folder.
    const folders = new Map<string, { dir: FsDirectory; taken: Set<string> }>();
    const folderFor = async (label: string) => {
      const existing = folders.get(label);
      if (existing) return existing;

      const dir = label === '' ? destination : await destination.directory(label, true);
      const taken = new Set((await dir.list()).filter((e) => e.kind === 'file').map((e) => e.name));
      const entry = { dir, taken };
      folders.set(label, entry);
      return entry;
    };

    for (const item of items) {
      if (signal?.aborted) {
        progress.phase = 'cancelled';
        onProgress?.({ ...progress });
        return { ...progress };
      }

      try {
        const label =
          options.structure === 'by-bucket'
            ? safeFolderName(bucketNames.get(item.bucketId ?? -1) ?? 'Unsorted')
            : '';
        const { dir, taken } = await folderFor(label);

        let name = item.filename;
        if (taken.has(name)) {
          if (options.conflict === 'skip') {
            progress.skipped++;
            progress.done++;
            onProgress?.({ ...progress });
            continue;
          }
          if (options.conflict === 'rename') name = uniqueName(name, taken);
        }

        if (item.sourceId === null) throw new Error('This photo has no source folder.');
        const source = await this.resolve(item.sourceId);
        if (!source) throw new Error('That folder is no longer available.');

        const file = await source.file(item.relPath);
        let bytes = await file.bytes();

        const shouldRotate =
          options.applyRotation && item.rotation !== 0 && isJpegName(item.filename);

        if (shouldRotate) {
          const result = rotateJpeg(bytes, item.rotation);
          bytes = result.bytes;
          if (!result.applied) {
            progress.errors.push({
              filename: item.filename,
              error: 'Copied, but the rotation could not be written to this file.',
            });
          }
        }

        await dir.write(name, bytes);
        taken.add(name);
        progress.done++;
      } catch (err) {
        progress.failed++;
        progress.done++;
        progress.errors.push({
          filename: item.filename,
          error: err instanceof Error ? err.message : String(err),
        });
      }

      onProgress?.({ ...progress });
    }

    progress.phase = 'done';
    progress.message = summarise(progress);
    onProgress?.({ ...progress });
    return { ...progress };
  }
}

function isJpegName(filename: string): boolean {
  const ext = extensionOf(filename);
  return ext === 'jpg' || ext === 'jpeg';
}

/** Bucket names become folder names, so strip anything a filesystem would reject. */
function safeFolderName(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|]/g, '-').trim();
  return cleaned.length > 0 ? cleaned : 'Unsorted';
}

function summarise(progress: ExportProgress): string {
  const copied = progress.done - progress.skipped - progress.failed;
  const parts = [`${copied} copied`];
  if (progress.skipped > 0) parts.push(`${progress.skipped} skipped`);
  if (progress.failed > 0) parts.push(`${progress.failed} failed`);
  return parts.join(' · ');
}
